import React, { useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Air from '@mui/icons-material/Air';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { relativeTime, updatedChipColor, isStale } from './hillCaptainUtils';

// ── PSI helpers ────────────────────────────────────────────────────────────

const getPsiStatus = (psi) => {
  const p = parseFloat(psi);
  if (psi === '' || psi === null || psi === undefined || isNaN(p)) return 'unknown';
  if (p >= 1500) return 'ready';
  if (p >= 450)  return 'check';
  return 'low';
};

const PSI_ROW_BORDER = {
  ready:   '#4caf50',
  check:   '#ff9800',
  low:     '#f44336',
  unknown: 'transparent',
};

const PSI_INPUT_BORDER = {
  ready:   '#64b5f6',
  check:   '#ffb74d',
  low:     '#ef9a9a',
  unknown: 'rgba(255, 255, 255, 0.3)',
};

const PSI_STATUS_LABEL = {
  ready:   'Ready',
  check:   'Check',
  low:     'Low',
  unknown: 'Unknown',
};

const PSI_CHIP_COLOR = {
  ready:   'success',
  check:   'warning',
  low:     'error',
  unknown: 'default',
};

// ── Component ──────────────────────────────────────────────────────────────

export const OxygenTracking = ({ oxygenData, onSave }) => {
  const [tanks,           setTanks]          = useState(oxygenData ?? []);
  const [isEditing,       setIsEditing]      = useState(false);
  const [error,           setError]          = useState(null);
  const [isSaving,        setIsSaving]       = useState(false);
  const [preEditSnapshot, setPreEditSnapshot] = useState(null);
  const [deletedIds,      setDeletedIds]     = useState([]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleRowClick = () => {
    if (!isEditing) {
      setPreEditSnapshot(tanks);
      setIsEditing(true);
    }
  };

  const handleAdd = () => {
    if (!isEditing) setPreEditSnapshot(tanks);
    setTanks(prev => [
      ...prev,
      { id: `new-${Date.now()}`, type: 'oxygen', location: '', psi: '', status: 'unknown' },
    ]);
    setIsEditing(true);
  };

  const handleDelete = (id) => {
    setTanks(prev => prev.filter(t => t.id !== id));
    if (!id.startsWith('new-')) setDeletedIds(prev => [...prev, id]);
  };

  const handleChange = (id, field, value) => {
    setTanks(prev => prev.map(t =>
      t.id === id ? { ...t, [field]: value } : t
    ));
    setError(null);
  };

  const handleCancel = () => {
    setTanks(preEditSnapshot ?? oxygenData ?? []);
    setPreEditSnapshot(null);
    setDeletedIds([]);
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const errors = await onSave(tanks, deletedIds);
      const now = Date.now();
      setTanks(current => current.map(t => ({
        ...t,
        lastUpdated: errors?.find(e => e.id === t.id) ? t.lastUpdated : now,
        status: errors?.find(e => e.id === t.id) ? 'error' : t.status,
      })));
      setDeletedIds([]);
      setIsEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to save oxygen tanks');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <Card>
      <CardHeader
        avatar={<Air />}
        title={<Typography variant="h6">Oxygen</Typography>}
        action={
          <Button size="small" startIcon={<Add />} onClick={handleAdd}>
            Add
          </Button>
        }
        sx={{ pb: 1 }}
      />
      <Divider />


      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mx: 1.5, mt: 1 }}>{error}</Alert>
      )}

      {/* Tank list */}
      <Box>
        {tanks.map((tank, index) => {
          const status     = getPsiStatus(tank.psi);
          const relTime    = relativeTime(tank.lastUpdated);
          const chipColor  = updatedChipColor(tank.lastUpdated);
          const stale      = isStale(tank.lastUpdated);

          return (
            <Box
              key={tank.id}
              data-testid="tank-row"
              data-status={status}
              onClick={handleRowClick}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.75, py: 1.25,
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                borderLeft: isEditing ? 'none' : `3px solid ${PSI_ROW_BORDER[status]}`,
                cursor: isEditing ? 'default' : 'pointer',
              }}
            >
              {/* Delete button (edit mode only) */}
              {isEditing && (
                <IconButton
                  data-testid={`delete-btn-${index}`}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleDelete(tank.id); }}
                  sx={{
                    width: 20, height: 20, p: 0,
                    bgcolor: '#c62828', color: 'white',
                    '&:hover': { bgcolor: '#b71c1c' },
                    flexShrink: 0,
                  }}
                >
                  <CancelIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}

              {/* Left: name + chips */}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.625 }}>

                {/* Name row */}
                {isEditing ? (
                  <TextField
                    size="small"
                    value={tank.location}
                    onChange={(e) => handleChange(tank.id, 'location', e.target.value)}
                    placeholder="Tank name"
                    inputProps={{ 'data-testid': `location-input-${index}` }}
                    sx={{ '& .MuiInputBase-input': { fontSize: 12, fontWeight: 600 } }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {stale && (
                      <WarningAmberIcon
                        data-testid="stale-icon"
                        sx={{ fontSize: 14, color: 'error.light', flexShrink: 0 }}
                      />
                    )}
                    <Typography fontSize={13} fontWeight={600}>
                      {tank.location}
                    </Typography>
                  </Box>
                )}

                {/* Chip row */}
                <Box sx={{ display: 'flex', gap: 0.625, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip
                    label={PSI_STATUS_LABEL[status]}
                    color={PSI_CHIP_COLOR[status]}
                    size="small"
                    sx={{ fontSize: 9, height: 18, fontWeight: 700 }}
                  />
                  {relTime && (
                    <Chip
                      label={relTime}
                      color={chipColor}
                      size="small"
                      sx={{ fontSize: 9, height: 18, fontWeight: 700 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Right: PSI value / input */}
              {isEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <TextField
                    type="number"
                    size="small"
                    value={tank.psi}
                    onChange={(e) => handleChange(tank.id, 'psi', e.target.value)}
                    inputProps={{
                      'data-testid': `psi-input-${index}`,
                      style: { width: 58, fontSize: 17, fontWeight: 700, textAlign: 'center' },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: PSI_INPUT_BORDER[status], borderWidth: 2 },
                        '&:hover fieldset': { borderColor: PSI_INPUT_BORDER[status] },
                        '&.Mui-focused fieldset': { borderColor: PSI_INPUT_BORDER[status] },
                      },
                    }}
                  />
                  <Typography fontSize={10} color="text.secondary" sx={{ alignSelf: 'flex-end', pb: 0.25 }}>
                    psi
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Typography fontSize={18} fontWeight={700}>{tank.psi}</Typography>
                  <Typography fontSize={10} color="text.secondary" sx={{ alignSelf: 'flex-end', pb: 0.25 }}>
                    psi
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Bottom bar — edit mode only */}
      {isEditing && (
        <Box sx={{
          borderTop: '1px solid', borderColor: 'divider',
          px: 1.5, py: 1.25,
          display: 'flex', gap: 1,
          position: 'sticky', bottom: 0,
          bgcolor: 'background.paper',
        }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isSaving}
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            size="small"
            sx={{ flex: 1 }}
          >
            {isSaving ? 'Saving\u2026' : 'Save All'}
          </Button>
        </Box>
      )}
    </Card>
  );
};
